/**
 * 认证服务 - 集成日志记录功能
 * 处理用户登录、登出、权限验证等认证相关功能
 */

import { httpClient } from './httpClient';
import { API_ENDPOINTS } from './api/config';
import { unifiedDictManager } from '../utils/unifiedDictManager';
import { clearGlobalDictCache } from '../hooks/useUnifiedDict';

// 本地类型定义
interface UserInfo {
  id: number;
  username: string;
  nickname: string;
  email: string;
  phone: string;
  avatar: string;
  roles: any[];
  permissions: string[];
  createTime: string;
  updateTime: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: UserInfo | null;
  token: string | null;
  refreshToken: string | null;
  permissions: string[];
}

interface LoginRequest {
  username: string;
  password: string;
}

class AuthService {
  private static instance: AuthService;
  private authState: AuthState = {
    isAuthenticated: false,
    user: null,
    token: null,
    refreshToken: null,
    permissions: [],
  };

  constructor() {
    this.initializeAuth();
  }

  /**
   * 获取单例实例
   */
  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * 初始化认证状态
   */
  private initializeAuth(): void {
    const token = localStorage.getItem('admin_token');
    const userInfo = localStorage.getItem('user_info');

    if (token && userInfo) {
      try {
        this.authState = {
          isAuthenticated: true,
          user: JSON.parse(userInfo),
          token,
          refreshToken: null,
          permissions: [],
        };
      } catch (error) {
        this.clearAuth();
      }
    }
  }

  /**
   * 用户登录
   */
  public async login(credentials: LoginRequest): Promise<any> {
    try {
      const response = await httpClient.post<any>(
        API_ENDPOINTS.AUTH.LOGIN,
        credentials
      );

      if (response.code === 200 && response.data) {
        const { token, userId, username, nickname, email, phone, avatar } = response.data;
        
        const userInfo: UserInfo = {
          id: userId || 0,
          username: username || '',
          nickname: nickname || '',
          email: email || '',
          phone: phone || '',
          avatar: avatar || '',
          roles: [],
          permissions: [],
          createTime: new Date().toISOString(),
          updateTime: new Date().toISOString(),
        };
        
        this.setAuthState({
          isAuthenticated: true,
          user: userInfo,
          token: token || '',
          refreshToken: null,
          permissions: [],
        });

        localStorage.setItem('admin_token', token || '');
        localStorage.setItem('user_info', JSON.stringify(userInfo));

        // 用户信息已存储到localStorage，后端拦截器可以从请求头中获取

        // 登录成功后清除菜单缓存，强制重新加载
        try {
          localStorage.removeItem('menu_data_cache');
          // 清除全局菜单缓存（包括所有用户的缓存）
          if (typeof window !== 'undefined') {
            const globalMenuDataMap = (window as any).__globalMenuDataMap__;
            const globalIsLoadedMap = (window as any).__globalIsLoadedMap__;
            if (globalMenuDataMap) {
              globalMenuDataMap.clear();
            }
            if (globalIsLoadedMap) {
              globalIsLoadedMap.clear();
            }
            // 清除loadingPromise，确保下次加载时重新创建
            delete (window as any).__loadingPromise__;
            // 通过自定义事件触发菜单重新加载
            window.dispatchEvent(new CustomEvent('menu:reload'));
          }
        } catch (error) {
          // 忽略清除缓存失败
        }

        // 登录成功后立即初始化所有字典数据
        try {
          const { unifiedDictManager } = await import('../utils/unifiedDictManager');
          await unifiedDictManager.initializeAllDicts();
        } catch (error) {
          // 不阻断登录流程，继续执行
        }
      }

      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * 用户登出
   */
  public async logout(): Promise<void> {
    try {
      const currentUser = this.getCurrentUser();
      
      await httpClient.post(API_ENDPOINTS.AUTH.LOGOUT, {});

      // 登出日志将由拦截器自动记录
    } catch (error) {
      // 忽略登出请求失败
    } finally {
      this.clearAuth();
      // 清除字典缓存
      this.clearDictCache();
    }
  }

  /**
   * 获取用户信息
   */
  public async getUserInfo(): Promise<UserInfo> {
    try {
      const response = await httpClient.get<UserInfo>(API_ENDPOINTS.AUTH.USERINFO);
      
      if (response.code === 200) {
        this.authState.user = response.data;
        localStorage.setItem('user_info', JSON.stringify(response.data));
        return response.data;
      } else {
        throw new Error(response.message || '获取用户信息失败');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * 获取客户端IP地址
   */
  private getClientIP(): string {
    // 这里可以通过第三方服务获取真实IP，或者使用后端返回的IP
    // 暂时返回本地IP标识
    return '127.0.0.1';
  }

  /**
   * 清除字典缓存
   */
  private clearDictCache(): void {
    try {
      unifiedDictManager.clearAllCache();
      clearGlobalDictCache();
    } catch (error) {
      // 忽略清除字典缓存失败
    }
  }

  /**
   * 预加载所有字典数据
   */
  private async preloadAllDicts(): Promise<void> {
    try {
      
      // 使用统一字典管理器进行批量预加载
      await unifiedDictManager.preloadAllDicts();
      
    } catch (error) {
      // 预加载失败不影响登录流程
    }
  }

  /**
   * 设置认证状态
   */
  private setAuthState(authData: any): void {
    this.authState = {
      isAuthenticated: authData.isAuthenticated,
      user: authData.user,
      token: authData.token,
      refreshToken: authData.refreshToken,
      permissions: authData.permissions || [],
    };
    // 触发状态变化事件
    this.emitAuthStateChange();
  }

  /**
   * 触发认证状态变化事件
   */
  private emitAuthStateChange(): void {
    const event = new CustomEvent('authStateChange', {
      detail: { ...this.authState }
    });
    window.dispatchEvent(event);
  }

  /**
   * 懒加载字典数据（仅在需要时调用）
   * 在进入数据维护页面时调用此方法
   */
  public async loadDictDataOnDemand(): Promise<void> {
    try {
      // 检查是否已登录
      if (!this.isAuthenticated()) {
        return;
      }

      // 检查本地是否已有字典缓存
      const cachedDicts = localStorage.getItem('dict_cache');
      if (cachedDicts) {
        return;
      }

      await this.preloadAllDicts();
    } catch (error) {
      // 忽略懒加载字典数据失败
    }
  }
  private clearAuth(): void {
    this.authState = {
      isAuthenticated: false,
      user: null,
      token: null,
      refreshToken: null,
      permissions: [],
    };

    localStorage.removeItem('admin_token');
    localStorage.removeItem('user_info');
    
    // 触发状态变化事件，通知所有监听者
    this.emitAuthStateChange();
  }

  /**
   * 检查是否已认证
   */
  public isAuthenticated(): boolean {
    return this.authState.isAuthenticated && !!this.authState.token;
  }

  /**
   * 获取当前用户信息
   */
  public getCurrentUser(): UserInfo | null {
    return this.authState.user;
  }

  /**
   * 获取认证token
   */
  public getToken(): string | null {
    return this.authState.token;
  }

  /**
   * 获取认证状态
   */
  public getAuthState(): AuthState {
    return { ...this.authState };
  }
}

// 创建认证服务实例
const authService = AuthService.getInstance();

export { authService };
export type { UserInfo, AuthState };
export default authService;